// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — MainStreet Operations Store (Zustand)
// Manages status checks, responses, damage reports, resource
// offers, posts, re-entry authorizations & passes — wired to
// Ignatius via the Cloudflare Worker proxy.
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, fetchRecords } from '../api/client';
import { createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../lib/api-switch';
import { useAuthStore } from './authStore';
import { logActivity } from '../lib/auditLog';

// ── Types ─────────────────────────────────────────────────────

export interface MSStatusCheck {
  id: number; Rid: number;
  incidentId: string; title: string; question: string;
  targetType: string; targetSectors: string; targetOrgs: string;
  deadline: string; status: string; createdAt: string; authorName: string;
}

export interface MSStatusResponse {
  id: number; Rid: number;
  statusCheckId: string; orgId: string; orgName: string;
  response: string; notes: string; respondedAt: string;
}

export interface MSDamageReport {
  id: number; Rid: number;
  orgId: string; orgName: string; incidentId: string;
  opStatus: string; damageTypes: string; description: string;
  severity: string; reportedBy: string; reportedAt: string; photos: string;
}

export interface MSResourceOffer {
  id: number; Rid: number;
  orgId: string; orgName: string; incidentId: string;
  offerType: string; description: string; quantity: string;
  status: string; offeredBy: string; createdAt: string; notes: string;
}

export interface MSPost {
  id: number; Rid: number;
  incidentId: string; content: string; postType: string;
  priority: string; authorName: string; createdAt: string;
  targetType: string; targetOrgIds: string; targetListId: string;
  parentPostId: string; readBy: string;
}

export interface MSReentryAuth {
  id: number; Rid: number;
  incidentId: string; title: string; targetType: string;
  targetSectors: string; targetOrgs: string;
  issuedBy: string; issuedAt: string; expiresAt: string;
  status: string; notes: string;
}

export interface MSReentryPass {
  id: number; Rid: number;
  authId: string; orgId: string; orgName: string;
  code: string; status: string;
  issuedAt: string; expiresAt: string; verifiedAt: string;
}

// ── Store Interface ───────────────────────────────────────────

interface MainStreetOpsStore {
  // State
  statusChecks: MSStatusCheck[];
  statusResponses: MSStatusResponse[];
  damageReports: MSDamageReport[];
  resourceOffers: MSResourceOffer[];
  posts: MSPost[];
  reentryAuths: MSReentryAuth[];
  reentryPasses: MSReentryPass[];
  loading: boolean;
  error: string | null;

  // Status Checks
  loadStatusChecks: () => Promise<void>;
  createStatusCheck: (data: Partial<MSStatusCheck>) => Promise<MSStatusCheck | null>;
  updateStatusCheck: (id: number, data: Partial<MSStatusCheck>) => Promise<void>;
  deleteStatusCheck: (id: number) => Promise<void>;

  // Status Responses
  loadStatusResponses: (checkId?: string) => Promise<void>;
  submitResponse: (data: Partial<MSStatusResponse>) => Promise<MSStatusResponse | null>;

  // Damage Reports
  loadDamageReports: () => Promise<void>;
  createDamageReport: (data: Partial<MSDamageReport>) => Promise<MSDamageReport | null>;
  updateDamageReport: (id: number, data: Partial<MSDamageReport>) => Promise<void>;
  deleteDamageReport: (id: number) => Promise<void>;

  // Resource Offers
  loadResourceOffers: () => Promise<void>;
  createResourceOffer: (data: Partial<MSResourceOffer>) => Promise<MSResourceOffer | null>;
  updateOfferStatus: (id: number, status: string) => Promise<void>;
  deleteResourceOffer: (id: number) => Promise<void>;

  // Posts
  loadPosts: () => Promise<void>;
  createPost: (data: Partial<MSPost>) => Promise<MSPost | null>;
  deletePost: (id: number) => Promise<void>;

  // Re-entry Authorizations
  loadReentryAuths: () => Promise<void>;
  createReentryAuth: (data: Partial<MSReentryAuth>) => Promise<MSReentryAuth | null>;
  updateReentryAuth: (id: number, data: Partial<MSReentryAuth>) => Promise<void>;

  // Re-entry Passes
  loadReentryPasses: () => Promise<void>;
  issuePass: (data: Partial<MSReentryPass>) => Promise<MSReentryPass | null>;
  bulkIssuePasses: (passes: Array<Partial<MSReentryPass>>, onProgress?: (done: number, total: number) => void) => Promise<number>;
  verifyPass: (code: string) => Promise<MSReentryPass | null>;
  updatePassStatus: (id: number, status: string) => Promise<void>;
}

// ── Helper: get auth token ────────────────────────────────────

function getToken(): string | undefined {
  return useAuthStore.getState().token ?? undefined;
}

// ── Row mappers ───────────────────────────────────────────────

// Field alias constants for V2 field IDs
const FSC = FIELDS.msStatusChecks;
const FSRP = FIELDS.msStatusResponses;
const FDR = FIELDS.msDamageReports;
const FRO = FIELDS.msResourceOffers;
const FRA = FIELDS.msReentryAuthorizations;
const FRP = FIELDS.msReentryPasses;
const FP = FIELDS.msPosts;

function mapStatusCheck(row: Record<string, unknown>): MSStatusCheck {
  const id = Number(row.record_id) || Number(row.id) || 0;
  return {
    id, Rid: id,
    incidentId: String(row[FSC.incidentId] ?? ''),
    title: String(row[FSC.title] ?? ''),
    question: String(row[FSC.message] ?? ''),
    targetType: '',
    targetSectors: '',
    targetOrgs: '',
    deadline: '',
    status: 'sent',
    createdAt: String(row[FSC.sentAt] ?? ''),
    authorName: '',
  };
}

function mapStatusResponse(row: Record<string, unknown>): MSStatusResponse {
  const id = Number(row.record_id) || Number(row.id) || 0;
  return {
    id, Rid: id,
    statusCheckId: String(row[FSRP.checkId] ?? ''),
    orgId: String(row[FSRP.orgId] ?? ''),
    orgName: String(row[FSRP.orgName] ?? ''),
    response: String(row[FSRP.opStatus] ?? ''),
    notes: String(row[FSRP.notes] ?? ''),
    respondedAt: String(row[FSRP.respondedAt] ?? ''),
  };
}

function mapDamageReport(row: Record<string, unknown>): MSDamageReport {
  const id = Number(row.record_id) || Number(row.id) || 0;
  const rawDamageType = String(row[FDR.damageType] ?? '');
  return {
    id, Rid: id,
    orgId: String(row[FDR.orgId] ?? ''),
    orgName: '',
    incidentId: '',
    opStatus: String(row[FDR.status] ?? ''),
    damageTypes: (() => {
      if (rawDamageType.startsWith('[')) {
        try { return (JSON.parse(rawDamageType) as string[]).join(','); } catch { /* fall through */ }
      }
      return rawDamageType;
    })(),
    description: String(row[FDR.description] ?? ''),
    severity: String(row[FDR.severity] ?? ''),
    reportedBy: '',
    reportedAt: String(row[FDR.reportDate] ?? ''),
    photos: '',
  };
}

function mapResourceOffer(row: Record<string, unknown>): MSResourceOffer {
  const id = Number(row.record_id) || Number(row.id) || 0;
  return {
    id, Rid: id,
    orgId: String(row[FRO.orgId] ?? ''),
    orgName: String(row[FRO.contactName] ?? ''),
    incidentId: '',
    offerType: String(row[FRO.resourceType] ?? ''),
    description: String(row[FRO.description] ?? ''),
    quantity: String(row[FRO.quantity] ?? ''),
    status: String(row[FRO.status] ?? ''),
    offeredBy: String(row[FRO.contactName] ?? ''),
    createdAt: String(row.created_at ?? ''),
    notes: '',
  };
}

function mapPost(row: Record<string, unknown>): MSPost {
  const id = Number(row.record_id) || Number(row.id) || 0;
  return {
    id, Rid: id,
    incidentId: String(row[FP.incidentId] ?? ''),
    content: String(row[FP.content] ?? ''),
    postType: String(row[FP.postType] ?? ''),
    priority: String(row[FP.priority] ?? ''),
    authorName: String(row[FP.authorName] ?? ''),
    createdAt: String(row[FP.createdAt] ?? row.created_at ?? ''),
    targetType: String(row[FP.targetType] ?? ''),
    targetOrgIds: String(row[FP.targetOrgIds] ?? ''),
    targetListId: String(row[FP.targetListId] ?? ''),
    parentPostId: String(row[FP.parentPostId] ?? ''),
    readBy: String(row[FP.readBy] ?? ''),
  };
}

function mapReentryAuth(row: Record<string, unknown>): MSReentryAuth {
  const id = Number(row.record_id) || Number(row.id) || 0;
  return {
    id, Rid: id,
    incidentId: String(row[FRA.incidentId] ?? ''),
    title: String(row[FRA.title] ?? ''),
    targetType: '',
    targetSectors: '',
    targetOrgs: '',
    issuedBy: String(row[FRA.issuedBy] ?? ''),
    issuedAt: String(row[FRA.validFrom] ?? ''),
    expiresAt: String(row[FRA.validTo] ?? ''),
    status: String(row[FRA.status] ?? ''),
    notes: String(row[FRA.notes] ?? ''),
  };
}

function mapReentryPass(row: Record<string, unknown>): MSReentryPass {
  const id = Number(row.record_id) || Number(row.id) || 0;
  return {
    id, Rid: id,
    authId: String(row[FRP.authId] ?? ''),
    orgId: String(row[FRP.orgId] ?? ''),
    orgName: String(row[FRP.orgName] ?? ''),
    code: String(row[FRP.passCode] ?? ''),
    status: String(row[FRP.status] ?? ''),
    issuedAt: String(row[FRP.issuedAt] ?? ''),
    expiresAt: String(row[FRP.expiresAt] ?? ''),
    verifiedAt: String(row[FRP.verifiedAt] ?? ''),
  };
}

// ── Store ─────────────────────────────────────────────────────

export const useMainStreetOpsStore = create<MainStreetOpsStore>((set, get) => ({
  statusChecks: [],
  statusResponses: [],
  damageReports: [],
  resourceOffers: [],
  posts: [],
  reentryAuths: [],
  reentryPasses: [],
  loading: false,
  error: null,

  // ── Status Checks ───────────────────────────────────────────

  loadStatusChecks: async () => {
    set({ loading: true, error: null });
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.msStatusChecks, {
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ statusChecks: (res.records ?? []).map(mapStatusCheck), loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load status checks' });
    }
  },

  createStatusCheck: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msStatusChecks;
      const fields: Array<[string, string]> = [
        [F.incidentId, data.incidentId ?? ''],
        [F.title, data.title ?? ''],
        [F.message, data.question ?? ''],
        [F.sentAt, data.createdAt ?? new Date().toISOString()],
      ];
      const rid = await createRecord(TABLES.msStatusChecks, fields, token);
      await get().loadStatusChecks();
      return get().statusChecks.find(sc => sc.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create status check' });
      return null;
    }
  },

  updateStatusCheck: async (id, data) => {
    const token = getToken();
    const F = FIELDS.msStatusChecks;
    const fields: Array<[string, string]> = [];
    if (data.incidentId !== undefined) fields.push([F.incidentId, data.incidentId]);
    if (data.title !== undefined) fields.push([F.title, data.title]);
    if (data.question !== undefined) fields.push([F.message, data.question]);
    if (data.createdAt !== undefined) fields.push([F.sentAt, data.createdAt]);
    if (fields.length > 0) {
      await updateRecord(TABLES.msStatusChecks, id, fields, token);
      await get().loadStatusChecks();
    }
  },

  deleteStatusCheck: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.msStatusChecks, id, token);
    set({ statusChecks: get().statusChecks.filter(sc => sc.id !== id) });
  },

  // ── Status Responses ────────────────────────────────────────

  loadStatusResponses: async (checkId?: string) => {
    set({ loading: true, error: null });
    try {
      const filters = checkId
        ? [{ field: FSRP.checkId, operator: 'eq' as const, value: checkId }]
        : [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.msStatusResponses, {
        filters,
        limit: 500,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ statusResponses: res.records.map(mapStatusResponse), loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load status responses' });
    }
  },

  submitResponse: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msStatusResponses;
      const fields: Array<[string, string]> = [
        [F.checkId, data.statusCheckId ?? ''],
        [F.orgId, data.orgId ?? ''],
        [F.orgName, data.orgName ?? ''],
        [F.opStatus, data.response ?? ''],
        [F.notes, data.notes ?? ''],
        [F.respondedAt, data.respondedAt ?? ''],
      ];
      const rid = await createRecord(TABLES.msStatusResponses, fields, token);
      await get().loadStatusResponses();
      return get().statusResponses.find(r => r.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to submit response' });
      return null;
    }
  },

  // ── Damage Reports ──────────────────────────────────────────

  loadDamageReports: async () => {
    set({ loading: true, error: null });
    try {
      const res = await queryRecords<Record<string, unknown>>(TABLES.msDamageReports, {
        filters: [],
        limit: 500,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ damageReports: res.records.map(mapDamageReport), loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load damage reports' });
    }
  },

  createDamageReport: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msDamageReports;
      const fields: Array<[string, string]> = [
        [F.orgId, data.orgId ?? ''],
        [F.damageType, data.damageTypes ?? ''],
        [F.description, data.description ?? ''],
        [F.severity, data.severity ?? ''],
        [F.reportDate, data.reportedAt ?? new Date().toISOString()],
        [F.status, data.opStatus ?? ''],
        [F.notes, [data.orgName, data.reportedBy].filter(Boolean).join(' | ')],
      ];
      const rid = await createRecord(TABLES.msDamageReports, fields, token);
      await get().loadDamageReports();
      return get().damageReports.find(dr => dr.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create damage report' });
      return null;
    }
  },

  updateDamageReport: async (id, data) => {
    const token = getToken();
    const F = FIELDS.msDamageReports;
    const fields: Array<[string, string]> = [];
    if (data.orgId !== undefined) fields.push([F.orgId, data.orgId]);
    if (data.opStatus !== undefined) fields.push([F.status, data.opStatus]);
    if (data.damageTypes !== undefined) fields.push([F.damageType, data.damageTypes]);
    if (data.description !== undefined) fields.push([F.description, data.description]);
    if (data.severity !== undefined) fields.push([F.severity, data.severity]);
    if (data.reportedAt !== undefined) fields.push([F.reportDate, data.reportedAt]);
    if (fields.length > 0) {
      await updateRecord(TABLES.msDamageReports, id, fields, token);
      await get().loadDamageReports();
    }
  },

  deleteDamageReport: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.msDamageReports, id, token);
    set({ damageReports: get().damageReports.filter(dr => dr.id !== id) });
  },

  // ── Resource Offers ─────────────────────────────────────────

  loadResourceOffers: async () => {
    set({ loading: true, error: null });
    try {
      const res = await queryRecords<Record<string, unknown>>(TABLES.msResourceOffers, {
        filters: [],
        limit: 500,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ resourceOffers: res.records.map(mapResourceOffer), loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load resource offers' });
    }
  },

  createResourceOffer: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msResourceOffers;
      const fields: Array<[string, string]> = [
        [F.orgId, data.orgId ?? ''],
        [F.resourceType, data.offerType ?? ''],
        [F.description, data.description ?? ''],
        [F.quantity, data.quantity ?? ''],
        [F.status, data.status ?? 'offered'],
        [F.contactName, data.offeredBy ?? data.orgName ?? ''],
      ];
      const rid = await createRecord(TABLES.msResourceOffers, fields, token);
      await get().loadResourceOffers();
      return get().resourceOffers.find(ro => ro.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create resource offer' });
      return null;
    }
  },

  updateOfferStatus: async (id, status: string) => {
    const token = getToken();
    const F = FIELDS.msResourceOffers;
    await updateRecord(TABLES.msResourceOffers, id, [[F.status, status]], token);
    await get().loadResourceOffers();
  },

  deleteResourceOffer: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.msResourceOffers, id, token);
    set({ resourceOffers: get().resourceOffers.filter(ro => ro.id !== id) });
  },

  // ── Posts ───────────────────────────────────────────────────

  loadPosts: async () => {
    set({ loading: true, error: null });
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.msPosts, {
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ posts: (res.records ?? []).map(mapPost), loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load posts' });
    }
  },

  createPost: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msPosts;
      const fields: Array<[string, string]> = [
        [F.incidentId, data.incidentId ?? ''],
        [F.content, data.content ?? ''],
        [F.postType, data.postType ?? ''],
        [F.priority, data.priority ?? ''],
        [F.authorName, data.authorName ?? ''],
        [F.createdAt, data.createdAt ?? ''],
        [F.targetType, data.targetType ?? ''],
        [F.targetOrgIds, data.targetOrgIds ?? ''],
        [F.targetListId, data.targetListId ?? ''],
        [F.parentPostId, data.parentPostId ?? ''],
      ];
      const rid = await createRecord(TABLES.msPosts, fields, token);
      await get().loadPosts();
      return get().posts.find(p => p.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create post' });
      return null;
    }
  },

  deletePost: async (id) => {
    const token = getToken();
    await deleteRecord(TABLES.msPosts, id, token);
    set({ posts: get().posts.filter(p => p.id !== id) });
  },

  // ── Re-entry Authorizations ─────────────────────────────────

  loadReentryAuths: async () => {
    set({ loading: true, error: null });
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.msReentryAuthorizations, { limit: 500 });
      const inc = useAuthStore.getState().activeIncident;
      const mapped = (res.records ?? [])
        .map(mapReentryAuth)
        // Filter out broken migrated records (empty title means migration didn't copy field values)
        .filter(a => a.title.trim() !== '');
      const filtered = inc
        ? mapped.filter(a => a.incidentId === String(inc.id) || a.incidentId === '')
        : mapped;
      set({ reentryAuths: filtered, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load re-entry authorizations' });
    }
  },

  createReentryAuth: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msReentryAuths;
      const fields: Array<[string, string]> = [
        [F.incidentId, data.incidentId ?? ''],
        [F.title, data.title ?? ''],
        [F.issuedBy, data.issuedBy ?? ''],
        [F.validFrom, data.issuedAt ?? ''],
        [F.validTo, data.expiresAt ?? ''],
        [F.status, data.status ?? ''],
        [F.notes, data.notes ?? ''],
      ];
      const rid = await createRecord(TABLES.msReentryAuthorizations, fields, token);
      await get().loadReentryAuths();
      return get().reentryAuths.find(ra => ra.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create re-entry authorization' });
      return null;
    }
  },

  updateReentryAuth: async (id, data) => {
    const token = getToken();
    const F = FIELDS.msReentryAuths;
    const fields: Array<[string, string]> = [];
    if (data.incidentId !== undefined) fields.push([F.incidentId, data.incidentId]);
    if (data.title !== undefined) fields.push([F.title, data.title]);
    if (data.issuedBy !== undefined) fields.push([F.issuedBy, data.issuedBy]);
    if (data.issuedAt !== undefined) fields.push([F.validFrom, data.issuedAt]);
    if (data.expiresAt !== undefined) fields.push([F.validTo, data.expiresAt]);
    if (data.status !== undefined) fields.push([F.status, data.status]);
    if (data.notes !== undefined) fields.push([F.notes, data.notes]);
    if (fields.length > 0) {
      await updateRecord(TABLES.msReentryAuthorizations, id, fields, token);
      await get().loadReentryAuths();
    }
  },

  // ── Re-entry Passes ─────────────────────────────────────────

  loadReentryPasses: async () => {
    set({ loading: true, error: null });
    try {
      const res = await queryRecords<Record<string, unknown>>(TABLES.msReentryPasses, {
        filters: [],
        limit: 500,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ reentryPasses: res.records.map(mapReentryPass), loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load re-entry passes' });
    }
  },

  issuePass: async (data) => {
    try {
      const token = getToken();
      const F = FIELDS.msReentryPasses;
      const fields: Array<[string, string]> = [
        [F.authId, data.authId ?? ''],
        [F.orgId, data.orgId ?? ''],
        [F.orgName, data.orgName ?? ''],
        [F.passCode, data.code ?? ''],
        [F.status, data.status ?? ''],
        [F.issuedAt, data.issuedAt ?? ''],
        [F.expiresAt, data.expiresAt ?? ''],
        [F.verifiedAt, data.verifiedAt ?? ''],
      ];
      const rid = await createRecord(TABLES.msReentryPasses, fields, token);
      await get().loadReentryPasses();

      try {
        const { useWorkflowStore } = await import('./workflowStore');
        // Find a template matching 'reentry_requested' and start it
        const tpl = useWorkflowStore.getState().templates.find(t => t.triggerType === 'reentry_requested' && t.isActive);
        if (tpl) {
          await useWorkflowStore.getState().startWorkflow(
            tpl.id, 'mainstreet', String(rid),
            `${data.orgName ?? 'Pass'} — ${data.code ?? ''}`,
            useAuthStore.getState().user?.email || 'system',
            data as Record<string, unknown>,
          );
        }
      } catch (e) { console.warn('[MainStreet] Workflow trigger failed:', e); }

      logActivity('mainstreet', 'create', 'reentry_pass', String(rid), `Pass issued: ${data.code || ''} to ${data.orgName || ''}`);
      return get().reentryPasses.find(rp => rp.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to issue pass' });
      return null;
    }
  },

  bulkIssuePasses: async (passes, onProgress) => {
    const token = getToken();
    const F = FIELDS.msReentryPasses;
    const BATCH_SIZE = 8;
    let issued = 0;

    for (let i = 0; i < passes.length; i += BATCH_SIZE) {
      const batch = passes.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(async (data) => {
        try {
          const fields: Array<[string, string]> = [
            [F.authId, data.authId ?? ''],
            [F.orgId, data.orgId ?? ''],
            [F.orgName, data.orgName ?? ''],
            [F.passCode, data.code ?? ''],
            [F.status, data.status ?? 'issued'],
            [F.issuedAt, data.issuedAt ?? ''],
            [F.expiresAt, data.expiresAt ?? ''],
            [F.verifiedAt, data.verifiedAt ?? ''],
          ];
          await createRecord(TABLES.msReentryPasses, fields, token);
          issued++;
        } catch { /* skip failed */ }
      }));
      onProgress?.(Math.min(i + BATCH_SIZE, passes.length), passes.length);
    }

    // Single reload at end
    await get().loadReentryPasses();
    return issued;
  },

  verifyPass: async (code: string) => {
    // Query server-side by pass code
    const F = FIELDS.msReentryPasses;
    const res = await queryRecords<Record<string, unknown>>(TABLES.msReentryPasses, {
      filters: [{ field: F.passCode, operator: 'eq' as const, value: code }],
      limit: 1,
    });
    const match = res.records[0] ? mapReentryPass(res.records[0]) : null;
    if (match) {
      const token = getToken();
      await updateRecord(TABLES.msReentryPasses, match.id, [
        [F.verifiedAt, new Date().toISOString()],
      ], token);
      await get().loadReentryPasses();
      return get().reentryPasses.find(rp => rp.id === match.id) ?? null;
    }
    return null;
  },

  updatePassStatus: async (id, status) => {
    const token = getToken();
    const F = FIELDS.msReentryPasses;
    const pass = get().reentryPasses.find(p => p.id === id);
    await updateRecord(TABLES.msReentryPasses, id, [[F.status, status]], token);
    await get().loadReentryPasses();
    logActivity('mainstreet', status === 'revoked' ? 'revoke' : status === 'issued' ? 'reinstate' : 'update', 'reentry_pass', String(id), `Pass ${pass?.code || ''} ${status} — ${pass?.orgName || ''}`);
  },
}));
